import DatePicker from "@/components/DatePicker";
import { toaster } from "@/components/ui/toaster";
import { date, now } from "@/helpers/time";
import { useMerchant } from "@/hooks/useMerchant";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Heading,
  Input,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { formatDocument } from "../../screenings/columns";
import { formatCalendarDate } from "../columns";
import { enrollmentCutoffDate, isEnrolledThisMonth } from "../enrollment";
import { Screening } from "../../screenings/types";
import { useProtectTRPC } from "../../trpc";
import { useProtectMerchant } from "../../useProtectMerchant";
import CoverageLines, {
  type IntegralOption,
  type PenaltyMonthsOption,
} from "./CoverageLines";

type Props = {
  open: boolean;
  onClose: () => void;
  screening?: Screening;
};

const defaultEndDate = (start: string) =>
  date(start).add(12, "month").format("YYYY-MM-DD");

const CreateContractModal = ({
  open,
  onClose,
  screening,
}: Props): JSX.Element => {
  const trpc = useProtectTRPC();
  const queryClient = useQueryClient();
  const { merchant } = useMerchant();
  const { protectMerchant } = useProtectMerchant();
  const formatCurrency = useNumberFormatter("es-CO", "COP", "currency");

  const [contractNumber, setContractNumber] = useState("");
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [isEndDateEdited, setIsEndDateEdited] = useState(false);
  const [integralAmount, setIntegralAmount] = useState<IntegralOption>(0);
  const [penaltyMonths, setPenaltyMonths] = useState<PenaltyMonthsOption>(0);

  useEffect(() => {
    const start = open ? screening?.rentalsContractStartDate : undefined;
    const end = open ? screening?.rentalsContractEndDate : undefined;
    setContractNumber(open ? screening?.rentalsContract ?? "" : "");
    setStartDate(start);
    setEndDate(end ?? (start ? defaultEndDate(start) : undefined));
    setIsEndDateEdited(!!end);
    setIntegralAmount(0);
    setPenaltyMonths(0);
  }, [open, screening]);

  const handleStartDateChange = (value: string | undefined) => {
    setStartDate(value);
    if (isEndDateEdited) return;
    setEndDate(value ? defaultEndDate(value) : undefined);
  };

  const handleEndDateChange = (value: string | undefined) => {
    setEndDate(value);
    setIsEndDateEdited(!!value);
  };

  const today = now();
  const enrolledThisMonth = isEnrolledThisMonth(today);
  const enrolledAt = enrolledThisMonth
    ? today.format("YYYY-MM-DD")
    : today.add(1, "month").startOf("month").format("YYYY-MM-DD");
  const cutoffDate = enrollmentCutoffDate(today).format("YYYY-MM-DD");

  const { mutateAsync: createContract, isPending } = useMutation({
    ...trpc.contracts.create.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.screenings.list.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.contracts.list.queryKey(),
      });
      toaster.create({
        title: "Contrato ingresado",
        description:
          "Se creó la fianza y se generó el certificado. Lo encuentras en la pestaña Contratos.",
        type: "success",
        duration: 6000,
        meta: { closable: true },
      });
      onClose();
    },
    onError: () =>
      toaster.create({
        title: "Error",
        description: "Hubo un error al ingresar el contrato.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      }),
  });

  const isValid =
    !!merchant?.id &&
    !!screening &&
    contractNumber.trim().length > 0 &&
    !!startDate &&
    !!endDate &&
    endDate > startDate;

  const handleSubmit = async () => {
    if (!isValid || !merchant || !screening || !startDate || !endDate) return;

    await createContract({
      rentalsMerchantId: merchant.id,
      screeningId: screening.id,
      contractNumber: contractNumber.trim(),
      rentalsContractStartDate: startDate,
      rentalsContractEndDate: endDate,
      integralAmount: integralAmount === 0 ? undefined : integralAmount,
      penaltyMonths: penaltyMonths === 0 ? undefined : penaltyMonths,
    });
  };

  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={(e) => !e.open && !isPending && onClose()}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
              <Dialog.Title>
                <Heading>Ingresar contrato afianzado</Heading>
                <Text textStyle="sm" color="gray.500" fontWeight="normal">
                  Desde un estudio aprobado
                </Text>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton marginTop="1" disabled={isPending} />
            </Dialog.CloseTrigger>
            <Dialog.Body py={6}>
              {screening && (
                <VStack gap={4} align="stretch">
                  <Box bg="gray.50" borderRadius="md" p={3}>
                    <Text fontWeight={600}>{screening.tenantName}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {formatDocument(screening)}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {screening.propertyAddress || "Sin dirección"}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Canon mensual: {formatCurrency(screening.monthlyRent)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight={600} mb={2}>
                      Número de contrato*
                    </Text>
                    <Input
                      value={contractNumber}
                      onChange={(e) => setContractNumber(e.target.value)}
                      placeholder="Ej: 2513-A"
                    />
                  </Box>
                  <Box>
                    <Text fontWeight={600} mb={2}>
                      Inicio del contrato de arrendamiento*
                    </Text>
                    <DatePicker
                      placeholder="Fecha"
                      value={startDate}
                      setValue={handleStartDateChange}
                    />
                  </Box>
                  <Box>
                    <Text fontWeight={600} mb={2}>
                      Fin del contrato de arrendamiento*
                    </Text>
                    <DatePicker
                      placeholder="Fecha"
                      value={endDate}
                      setValue={handleEndDateChange}
                    />
                  </Box>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight={600}
                      textTransform="uppercase"
                      letterSpacing="wider"
                      color="gray.500"
                      mb={2}
                    >
                      Líneas de cobertura
                    </Text>
                    <CoverageLines
                      monthlyRent={screening.monthlyRent}
                      rentCoverageMonths={protectMerchant?.coverageMonths}
                      integralAmount={integralAmount}
                      onIntegralAmountChange={setIntegralAmount}
                      penaltyMonths={penaltyMonths}
                      onPenaltyMonthsChange={setPenaltyMonths}
                    />
                  </Box>
                  <Box
                    bg={enrolledThisMonth ? "#E3FFE4" : "#FFF7D6"}
                    borderRadius="md"
                    p={3}
                  >
                    <Text fontSize="sm" fontWeight={600}>
                      {enrolledThisMonth
                        ? `Ingreso a la fianza: hoy, ${formatCalendarDate(
                            enrolledAt
                          )}`
                        : `Queda preingresado · ingresa a la fianza el ${formatCalendarDate(
                            enrolledAt
                          )}`}
                    </Text>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Este mes, los contratos ingresados hasta el{" "}
                      {formatCalendarDate(cutoffDate)} quedan afianzados el
                      mismo día; después, ingresan el 1 del mes siguiente.
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="gray.600">
                    Al ingresar, se genera el certificado de fianza y queda
                    disponible para descargar en la pestaña Contratos.
                  </Text>
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer borderTop="1px solid #ebecef80">
              <Button variant="outline" onClick={onClose} disabled={isPending}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!isValid}
                loading={isPending}
              >
                Ingresar y generar certificado
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default CreateContractModal;
